/**
 * EpisodeCountdownOverlay — full-screen 3-2-1 countdown shown right
 * before the next episode auto-loads (flash reading, chunk reading,
 * TTS, and the mark-as-read path). Cancellable via the × button.
 *
 * Feb 12 2026 user spec: *"for flash reading, chunk and tts, do a
 * 3,2,1 countdown and start next episode"*.
 */
import React, { useEffect, useState } from 'react';
import { X, Play } from 'lucide-react';

const EpisodeCountdownOverlay = ({ open, nextEpisodeTitle, onComplete, onCancel }) => {
  const [n, setN] = useState(3);

  // Reset to 3 every time the overlay re-opens.
  useEffect(() => {
    if (open) setN(3);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (n <= 0) {
      // Brief flash of the GO icon before firing the callback.
      const t = setTimeout(() => onComplete?.(), 600);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setN((cur) => cur - 1), 1000);
    return () => clearTimeout(t);
  }, [open, n, onComplete]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md grid place-items-center"
      data-testid="episode-countdown-overlay"
    >
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
        data-testid="episode-countdown-cancel"
        aria-label="Cancel auto-advance"
      >
        <X className="w-4 h-4 text-white" />
      </button>
      <div className="text-center px-6 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.4em] text-white/60">Next episode in</p>
        <p
          key={n}
          className="font-display text-[8rem] sm:text-[10rem] leading-none font-black text-amber-300 tabular-nums animate-pulse"
          data-testid="episode-countdown-number"
        >
          {n > 0 ? n : <Play className="w-24 h-24 mx-auto" />}
        </p>
        {nextEpisodeTitle && (
          <p className="text-sm text-white/80 max-w-xs mx-auto truncate" data-testid="episode-countdown-next-title">
            <span className="text-white/40">Up next: </span>{nextEpisodeTitle}
          </p>
        )}
        <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
          Tap the × to stop
        </p>
      </div>
    </div>
  );
};

export default EpisodeCountdownOverlay;
